import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { InterviewNodeData, getInterviewTypeMeta } from '../../../types/interviewBuilder';
import './nodeStyles.css';

export interface RoundNodeData {
  id: string;
  name: string;
  interview: InterviewNodeData | null;
  scheduledDate?: string;
  isSelected: boolean;
  isDragOver: boolean;
  isDragging: boolean;
  isMuted: boolean;
  canRemove: boolean;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onClick: () => void;
  onClearSlot: () => void;
  onRemoveRound: () => void;
}

const InterviewTypeIcon: React.FC<{ icon: string; color: string; size?: number }> = ({ icon, color, size = 20 }) => {
  switch (icon) {
    case 'triangle':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 8V4H8" />
          <rect x="4" y="8" width="16" height="12" rx="2" />
          <circle cx="9" cy="13" r="1" fill={color} />
          <circle cx="15" cy="13" r="1" fill={color} />
          <path d="M9 17h6" />
        </svg>
      );
    case 'square':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
          <line x1="14" y1="4" x2="10" y2="20" />
        </svg>
      );
    case 'diamond':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    default:
      return null;
  }
};

const RoundNode: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = data as unknown as RoundNodeData;
  const {
    name,
    interview,
    scheduledDate,
    isDragOver,
    isDragging,
    isMuted,
    canRemove,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragStart,
    onDragEnd,
    onClick,
    onClearSlot,
    onRemoveRound,
  } = nodeData;
  
  const meta = interview ? getInterviewTypeMeta(interview.interviewType) : null;
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  };
  
  return (
    <div
      className={`rf-node rf-node--round-pill ${
        interview ? 'rf-node--filled' : ''
      } ${
        isDragOver ? 'rf-node--drag-over' : ''
      } ${
        selected ? 'rf-node--selected' : ''
      } ${
        interview ? `rf-node--${interview.interviewType}` : ''
      } ${
        isDragging ? 'rf-node--dragging' : ''
      } ${
        isMuted ? 'rf-node--muted' : ''
      }`}
    >
      {/* Target handle on left side - centered */}
      <Handle
        type="target"
        position={Position.Left}
        className="rf-handle rf-handle--target rf-handle--pill-center"
      />
      
      <div
        className={`rf-pill ${
          interview ? 'rf-pill--filled' : ''
        } ${
          interview ? `rf-pill--${interview.interviewType}` : ''
        } ${
          isDragOver ? 'rf-pill--drag-over' : ''
        } ${
          selected ? 'rf-pill--selected' : ''
        } ${
          isDragging ? 'rf-pill--dragging' : ''
        } ${
          isMuted ? 'rf-pill--muted' : ''
        }`}
        draggable={!isMuted && !!interview}
        onDragStart={!isMuted ? onDragStart : undefined}
        onDragEnd={!isMuted ? onDragEnd : undefined}
        onDragOver={!isMuted ? onDragOver : undefined}
        onDragLeave={!isMuted ? onDragLeave : undefined}
        onDrop={!isMuted ? onDrop : undefined}
        onClick={!isMuted ? onClick : undefined}
      >
        {interview && meta ? (
          <>
            {/* Icon on left side */}
            <div className={`rf-pill__icon rf-pill__icon--${interview.interviewType}`}>
              <InterviewTypeIcon icon={meta.icon} color={meta.color} size={20} />
            </div>
            
            {/* Two-row text content */}
            <div className="rf-pill__content">
              <span className="rf-pill__title">{interview.label}</span>
              <span className="rf-pill__subtitle">
                {scheduledDate ? formatDate(scheduledDate) : name}
              </span>
            </div>
            
            {/* Checkmark badge - shows when interview is dropped */}
            <span className={`rf-pill__badge rf-pill__badge--complete rf-pill__badge--${interview.interviewType}`}>
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            
            {/* Remove button */}
            <button
              className="rf-pill__remove"
              onClick={(e) => {
                e.stopPropagation();
                onClearSlot();
              }}
            >
              ×
            </button>
          </>
        ) : (
          <>
            {/* Empty state - placeholder icon */}
            <div className="rf-pill__icon rf-pill__icon--empty">
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M12 8v8M8 12h8" />
              </svg>
            </div>
            
            {/* Empty text content */}
            <div className="rf-pill__content">
              <span className="rf-pill__title rf-pill__title--empty">Drop interview</span>
              <span className="rf-pill__subtitle rf-pill__subtitle--empty">{name}</span>
            </div>
            
            {canRemove && (
              <button
                className="rf-pill__remove rf-pill__remove--empty"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveRound();
                }}
              >
                ×
              </button>
            )}
          </>
        )}
      </div>
      
      {/* Source handle on right side - centered */}
      <Handle
        type="source"
        position={Position.Right}
        className="rf-handle rf-handle--source rf-handle--pill-center"
      />
    </div>
  );
};

export default memo(RoundNode);
